import React, { useEffect, useRef } from 'react';
import moment from 'moment';
import { useSelector, useDispatch } from 'react-redux';
import {
    components as Core,
    ICONS,
    getPagination,
    useTranslation,
    getTableProps,
    valueBuilder,
    BUTTON_TYPES,
    usePrevious
} from '../../../../../../core';
import { StyleSheet, colors, css, spacing, fonts } from '../../../../../../../styles';
import * as dashboardSelectors from '../../../../../selectors';
import {
    getRegistrations,
    downloadRegistrationsReport,
    removeRegistrations
} from '../../../../../actions';
import { useState } from 'react';

const COLUMNS = {
    companyEmail: 'companyEmail',
    registrationDate: 'registrationDate',
    trialExpire: 'trialExpire',
    region: 'region',
    country: 'country',
    isRemoved: 'isRemoved',
    removedDate: 'removedDate'
};

export const RegistrationsTable = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(getRegistrations());
    }, []);

    const isLoading = useSelector(dashboardSelectors.isLoadingRegistrationsData);
    const isRemoving = useSelector(dashboardSelectors.isRemovingRegistrations);
    const prevIsLoading = usePrevious(isLoading);
    const isLoadingReport = useSelector(dashboardSelectors.isLoadingDownloadingReport);
    const data = useSelector(dashboardSelectors.getRegistrations);
    const pagination = useSelector(dashboardSelectors.getPagination);

    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [searchText, setSearchText] = useState('');
    const tableStateRef = useRef({ pagination: {}, filters: {}, sorter: {} });

    useEffect(() => {
        if (prevIsLoading && !isLoading) {
            setSelectedRowKeys([]);
        }
    }, [isLoading]);

    const tableColumns = () => [
        {
            title: t('companyEmail'),
            key: COLUMNS.companyEmail,
            dataIndex: COLUMNS.companyEmail
        },
        {
            title: t('dashboard.registrationsTable.registrationDate'),
            key: COLUMNS.registrationDate,
            dataIndex: COLUMNS.registrationDate,
            render: valueBuilder.getLocalFullTime
        },
        {
            title: t('dashboard.registrationsTable.trialExpire'),
            key: COLUMNS.trialExpire,
            dataIndex: COLUMNS.trialExpire,
            render: renderTrialDateColumn
        },
        {
            title: t('region'),
            key: COLUMNS.region,
            dataIndex: COLUMNS.region,
            render: renderRegion
        },
        {
            title: t('country'),
            key: COLUMNS.country,
            dataIndex: COLUMNS.country
        },
        {
            title: t('dashboard.registrationsTable.isRemoved'),
            key: COLUMNS.isRemoved,
            dataIndex: COLUMNS.isRemoved,
            render: renderIsRemoved,
            align: 'center'
        },
        {
            title: t('dashboard.registrationsTable.removedDate'),
            key: COLUMNS.removedDate,
            dataIndex: COLUMNS.removedDate,
            render: valueBuilder.getLocalFullTime
        }
    ];

    const renderTrialDateColumn = (value, company) => {
        if (company.hasSubscription)
            return <span style={{ color: 'green' }}>Active Subscription</span>;
        const date = valueBuilder.getLocalFullTime(value);
        return moment().isBefore(date) ? date : <span style={{ color: 'red' }}>{date}</span>;
    };

    const renderRegion = value => t(`regionTypes.${value}`);

    const renderIsRemoved = value => (
        <span style={{ color: value ? colors.red : colors.green }}>
            {value ? t('yes') : t('no')}
        </span>
    );

    const footerButton = () => {
        return (
            <>
                <Core.Icon
                    className={css(styles.footerIcon)}
                    color={colors.white}
                    type={ICONS.download}
                />
                {t('export')}
            </>
        );
    };

    const onDownload = () => {
        dispatch(downloadRegistrationsReport());
    };

    const onRemove = () => {
        if (isRemoving || isLoading || selectedRowKeys.length === 0) return;
        const { pagination: paginationState, filters, sorter } = tableStateRef.current;
        dispatch(removeRegistrations(selectedRowKeys, {
            ...getTableProps(paginationState, filters, sorter),
            term: searchText
        }));
    };

    const getPaginationForTable = () => getPagination(pagination);

    const loadRegistrations = (page = null) => {
        const { pagination: paginationState, filters, sorter } = tableStateRef.current;
        const effectivePagination = page ? { current: page } : paginationState;
        dispatch(
            getRegistrations({
                ...getTableProps(effectivePagination, filters, sorter),
                term: searchText
            })
        );
    };

    const onChangeTable = (pagination, filters, sorter) => {
        tableStateRef.current = { pagination, filters, sorter };
        loadRegistrations();
    };

    const onSearch = () => {
        tableStateRef.current.pagination = {};
        loadRegistrations(1);
    };

    const onChangeSearchText = value => setSearchText(value);

    return (
        <div>
            <div className={css(styles.taleHeader)}>
                <Core.Button
                    isDisabled={isLoadingReport}
                    isLoading={isLoadingReport}
                    onPress={onDownload}
                    label={footerButton()}
                />
                {selectedRowKeys.length > 0 && (
                    <Core.PopConfirm
                        title={t('dashboard.overview.removeRegistrationsConfirm')}
                        onConfirm={onRemove}
                    >
                        <a>
                            <Core.Button
                                label={t('remove')}
                                icon={ICONS.remove}
                                type={BUTTON_TYPES.redOutline}
                                isLoading={isRemoving}
                                isDisabled={isRemoving || isLoading}
                            />
                        </a>
                    </Core.PopConfirm>
                )}
            </div>
            <Core.Table
                columns={tableColumns()}
                data={data}
                isLoading={isLoading}
                pagination={getPaginationForTable()}
                selectedRowKeys={selectedRowKeys}
                onSelectRows={setSelectedRowKeys}
                onChange={onChangeTable}
                hasSearch={true}
                searchText={searchText}
                onSearch={onSearch}
                onChangeSearchText={onChangeSearchText}
            />
        </div>
    );
};

const styles = StyleSheet.create({
    passive: {
        color: colors.yellow
    },
    detractor: {
        color: colors.red
    },
    promoter: {
        color: colors.green
    },
    footerIcon: {
        marginRight: spacing.s0
    },
    message: {
        ...fonts.mdBold,
        marginTop: spacing.s2
    },
    companyName: {
        ...fonts.smBold
    },
    taleHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: spacing.s2,
        marginBottom: spacing.s3
    }
});
